/**
 * Builds an anonymized, plain-text traction report from the admin analytics
 * data, for sharing with clubs / federations / sponsors. Contains only
 * aggregate numbers — no names, no user identities.
 */

import { AnalyticsData } from '../services/adminAnalytics';

const eur = (n: number | null | undefined): string =>
  n == null ? '—' : `€${n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (n: number | null | undefined): string =>
  n == null ? '—' : n.toLocaleString('nl-NL');

const pct = (part: number, whole: number): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—';

/**
 * @param data   Loaded analytics
 * @param dateLabel Human-readable date the report was generated (pass in — the
 *                  formatter stays pure and testable)
 */
export const buildTractionReport = (data: AnalyticsData, dateLabel: string): string => {
  const { users, items, funnel, traction, engagement } = data;

  const lines: string[] = [];
  lines.push('CLUBSWAP — PLATFORM TRACTIE');
  lines.push(dateLabel);
  lines.push('(geanonimiseerde, geaggregeerde cijfers)');
  lines.push('');

  lines.push('KERNCIJFERS');
  lines.push(`• Gebruikers: ${num(users.total)} (${num(users.new_30d)} nieuw in 30 dagen)`);
  lines.push(`• Actief (30d): ${num(users.active_30d)}`);
  lines.push(`• Advertenties: ${num(items.total)} (${num(items.sold)} verkocht)`);
  lines.push(`• GMV totaal: ${eur(traction.gmv_total)} · 30 dagen: ${eur(traction.gmv_30d)}`);
  // De verkoopprijs is optioneel. Zonder deze regel leest een buitenstaander de
  // GMV als een compleet bedrag, terwijl het een ondergrens is.
  lines.push(`• Prijs bekend bij ${num(traction.gmv_price_coverage_pct)}% van de verkopen — GMV is een ondergrens`);
  lines.push('');

  lines.push('FUNNEL (totaal)');
  lines.push(`• Advertenties geplaatst: ${num(funnel.listings)}`);
  lines.push(`• Bekeken (≥1 weergave): ${num(funnel.items_viewed)} (${pct(funnel.items_viewed, funnel.listings)})`);
  lines.push(`• Chats gestart: ${num(funnel.chats_started)} (${pct(funnel.chats_started, funnel.items_viewed)})`);
  lines.push(`• Verkocht: ${num(funnel.deals_completed)} (${pct(funnel.deals_completed, funnel.chats_started)})`);
  lines.push('');

  lines.push('MATCH VIA HET PLATFORM');
  lines.push(`• Verkocht aan een chatpartner: ${num(traction.deals_on_platform)} (${pct(traction.deals_on_platform, traction.deals_on_platform + traction.deals_off_platform)})`);
  lines.push(`• Verkocht buiten het platform: ${num(traction.deals_off_platform)}`);
  lines.push('');

  lines.push('LIQUIDITEIT & HERHAALGEBRUIK');
  lines.push(`• Mediane tijd tot verkoop: ${traction.median_days_to_sale != null ? `${traction.median_days_to_sale} dagen` : '—'}`);
  lines.push(`• Verkocht binnen 30 dagen: ${num(traction.pct_sold_within_30d)}%`);
  lines.push(`• Herhaalverkopers (≥2 verkopen): ${num(traction.repeat_sellers)}`);
  lines.push(`• Herhaalkopers (≥2 aankopen): ${num(traction.repeat_buyers)}`);
  lines.push(`• Conversie advertentie → verkoop: ${num(engagement.conversion_rate)}%`);
  lines.push('');

  if (traction.weekly.length > 0) {
    lines.push('GROEI PER WEEK (aanmeldingen / advertenties / verkopen)');
    for (const w of traction.weekly) {
      lines.push(`• ${w.week_start}: ${num(w.signups)} / ${num(w.new_listings)} / ${num(w.deals_completed)}`);
    }
  }

  return lines.join('\n');
};
